import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { PostsState, PostType, UsePostsReturn } from "./type";
import { socialDatabase } from "../../Data/Social/Database";
import * as socialAPI from "../../Data/Social";
import { CreatePostParams, UpdatePostParams } from "../../Data/Social/types";
import { contentFilter } from "../../Data/Social/ContentFilter";

export const validatePost = (post: any): post is PostType => {
    return post && 
           typeof post.postid === 'string' && 
           typeof post.addressid === 'string' && 
           typeof post.content === 'string' && 
           typeof post.created_at === 'number' && 
           typeof post.updated_at === 'number' &&
           typeof post.like_count === 'number' &&
           typeof post.reply_count === 'number' &&
           typeof post.bookmark_count === 'number';
};

// Posts state atom
export const postsState = atom<PostsState>({
    key: "postsState" + randomString(4),
    default: {
        items: {},
        loading: false,
        error: null
    },
    effects: [
        ({ setSelf }) => {
            const loadData = async () => {
                try {
                    await socialDatabase.init();
                    
                    // Load cached posts with validation
                    const cachedPosts = await socialDatabase.getAllPosts();
                    
                    if (cachedPosts.length > 0) {
                        // Validate posts before adding to state
                        const validPosts = cachedPosts.filter(validatePost);
                        
                        if (validPosts.length !== cachedPosts.length) {
                            console.warn(`Filtered out ${cachedPosts.length - validPosts.length} invalid posts from database`);
                        }
                        
                        // Create items mapping
                        const items = Object.fromEntries(validPosts.map(post => [post.postid, post]));
                        
                        setSelf({ 
                            items,
                            loading: false, 
                            error: null 
                        });
                    }
                } catch (error) {
                    console.error('Failed to load posts from database:', error);
                }
            };
            
            loadData();
        }
    ]
});

export const usePosts = (): UsePostsReturn => {
    const [state, setState] = useRecoilState(postsState);
    const resetPosts = useResetRecoilState(postsState);

    // Create a new post
    const createPost = useCallback(async (params: CreatePostParams): Promise<PostType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const post = await socialAPI.createPost(params);
            
            if (post) {
                
                if (validatePost(post)) {
                    await socialDatabase.savePosts([post]);
                    
                    setState(prev => {
                        const newItems = { ...prev.items, [post.postid]: post };
                        
                        // If this is a reply (has parent_postid), increment parent's reply_count
                        if (post.parent_postid && prev.items[post.parent_postid]) {
                            newItems[post.parent_postid] = {
                                ...prev.items[post.parent_postid],
                                reply_count: prev.items[post.parent_postid].reply_count + 1
                            };
                        }
                        
                        return {
                            ...prev,
                            items: newItems,
                            loading: false
                        };
                    });
                    
                    return post;
                }
            }
            
            setState(prev => ({ ...prev, error: "Failed to create post", loading: false }));
            return null;
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to create post",
                loading: false
            }));
            console.error('Failed to create post:', error);
            return null;
        }
    }, [setState]);

    // Sync posts from API
    const syncPost = useCallback(async (params: { postid: string }): Promise<PostType | null> => {
        if (!params.postid) {
            console.warn("syncPost called without postid");
            return null;
        }
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const post = await socialAPI.getPost(params.postid);
            
            if (post) {

                if (!validatePost(post)) {
                    setState(prev => ({
                        ...prev,
                        error: "Invalid post data received from API",
                        loading: false
                    }));
                    return null;
                };

                await socialDatabase.savePosts([post]);
                
                setState(prev => {
                    
                    return {
                        ...prev,
                        items: { ...prev.items, [post.postid]: post },
                        loading: false
                    };
                });

                return post;
            } else {
                setState(prev => ({ ...prev, loading: false }));
            }
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to sync posts",
                loading: false
            }));
            console.error('Failed to sync posts:', error);
        } finally {
            return null;
        }
    }, [setState]);

    // Update an existing post
    const updatePost = useCallback(async (params: UpdatePostParams): Promise<PostType | null> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const post = await socialAPI.updatePost(params);
            
            if (post) {
                
                if (validatePost(post)) {
                    await socialDatabase.savePosts([post]);
                    
                    setState(prev => ({
                        ...prev,
                        items: { ...prev.items, [post.postid]: post },
                        loading: false
                    }));
                    
                    return post;
                }
            }
            
            setState(prev => ({ ...prev, error: "Failed to update post", loading: false }));
            return null;
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to update post",
                loading: false
            }));
            console.error('Failed to update post:', error);
            return null;
        }
    }, [setState]);

    // Delete a post
    const deletePost = useCallback(async (postid: string): Promise<boolean> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const success = await socialAPI.deletePost({ postid });
            
            if (success) {
                await socialDatabase.deletePost(postid);
                
                setState(prev => {
                    const updatedItems = { ...prev.items };
                    const postToDelete = prev.items[postid];
                    
                    // If this is a reply (has parent_postid), decrement parent's reply_count
                    if (postToDelete?.parent_postid && prev.items[postToDelete.parent_postid]) {
                        updatedItems[postToDelete.parent_postid] = {
                            ...prev.items[postToDelete.parent_postid],
                            reply_count: Math.max(0, prev.items[postToDelete.parent_postid].reply_count - 1)
                        };
                    }
                    
                    delete updatedItems[postid];
                    
                    return {
                        ...prev,
                        items: updatedItems,
                        loading: false
                    };
                });
                
                return true;
            } else {
                setState(prev => ({ ...prev, error: "Failed to delete post", loading: false }));
                return false;
            }
        } catch (error) {
            setState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : "Failed to delete post",
                loading: false
            }));
            console.error('Failed to delete post:', error);
            return false;
        }
    }, [setState]);

    // Sync child posts for a specific parent post
    const syncChildPosts = useCallback(async (parentPostId: string): Promise<PostType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const childPosts = await socialAPI.listPosts({ parent_postid: parentPostId });
            
            if (childPosts && childPosts.length > 0) {
                
                const validChildPosts = childPosts.filter(validatePost);
                
                if (validChildPosts.length !== childPosts.length) {
                    console.warn(`Filtered out ${childPosts.length - validChildPosts.length} invalid child posts from API response`);
                }
                
                if (validChildPosts.length > 0) {
                    await socialDatabase.savePosts(validChildPosts);
                    
                    setState(prev => {
                        const newItems = Object.fromEntries(validChildPosts.map(post => [post.postid, post]));
                        
                        return {
                            ...prev,
                            items: { ...prev.items, ...newItems },
                            loading: false
                        };
                    });
                    
                    return validChildPosts;
                }
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return [];
        } catch (error) {
            setState(prev => ({ 
                ...prev, 
                error: error instanceof Error ? error.message : "Failed to sync child posts",
                loading: false
            }));
            console.error(`Failed to sync child posts for ${parentPostId}:`, error);
            return [];
        }
    }, [setState]);

    // Get cached child posts for a specific parent post
    const getChildPosts = useCallback((parentPostId: string): PostType[] => {
        return Object.values(state.items).filter(post => post.parent_postid === parentPostId);
    }, [state.items]);

    // Filter posts based on content filter
    const getFilteredPosts = useCallback(() => {
        const allPosts = Object.values(state.items);
        return contentFilter.filterPosts(allPosts);
    }, [state.items]);

    // Increment view count when a post is viewed
    const incrementViewCount = useCallback(async (postid: string): Promise<void> => {
        // Optimistically update the view count
        setState(prev => ({
            ...prev,
            items: {
                ...prev.items,
                [postid]: prev.items[postid] ? {
                    ...prev.items[postid],
                    view_count: prev.items[postid].view_count + 1
                } : prev.items[postid]
            }
        }));

        try {
            // Call API to record the view (if such endpoint exists)
            // await socialAPI.recordPostView({ postid });
            // Note: This would need to be implemented in the backend API
        } catch (error) {
            // Revert optimistic update on failure
            setState(prev => ({
                ...prev,
                items: {
                    ...prev.items,
                    [postid]: prev.items[postid] ? {
                        ...prev.items[postid],
                        view_count: Math.max(0, prev.items[postid].view_count - 1)
                    } : prev.items[postid]
                }
            }));
            console.error('Failed to record post view:', error);
        }
    }, [setState]);

    return {
        posts: state.items,
        filteredPosts: getFilteredPosts(),
        loading: state.loading,
        error: state.error,
        
        // Post functions
        createPost,
        updatePost,
        deletePost,
        syncPost,
        syncChildPosts,
        getChildPosts,
        incrementViewCount,

        reset: resetPosts
    };
};